import { zodResolver } from '@hookform/resolvers/zod'
import { InputFormField, PasswordFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  buttonVariants,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Skeleton,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { ClientOnly, createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useAuth } from '@web/src/hooks/use-auth'
import { useTurnstileWidgetSize } from '@web/src/hooks/use-turnstile-widget-size'
import { MIN_PASSWORD_LENGTH } from '@web/src/lib/auth/auth-const'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { useEffect, useRef, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Turnstile, useTurnstile } from 'react-turnstile'
import Cookies from 'universal-cookie'
import { z } from 'zod'
import { loginPageHeadFromLoaderData } from './login.seo'

const authFooterLinkClass = cn(buttonVariants({ variant: 'link', size: 'default' }))

export const Route = createFileRoute('/(public)/(main-layout)/auth/login')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: Login,
  validateSearch: z.object({
    redirectTo: z.string().optional(),
  }),
  head: () => loginPageHeadFromLoaderData(),
})

function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const turnstile = useTurnstile()
  const { loginWithCredentials, isAuthenticated } = useAuth()
  const { redirectTo } = useSearch({ strict: false })

  const formSchema = z.object({
    email: z.email({ message: t('Please enter a valid email address') }),
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `${t('Password must be at least')} ${MIN_PASSWORD_LENGTH} ${t('characters long')}`),
  })
  type FormValues = z.infer<typeof formSchema>

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  })

  const [token, setToken] = useState('')
  const turnstileSize = useTurnstileWidgetSize()

  const cookies = new Cookies()

  const isCaptchaDisabled = getAppConfig().CAPTCHA_DISABLE === true

  useEffect(() => {
    if (isAuthenticated) {
      if (redirectTo) {
        navigate({ to: redirectTo })
      } else {
        navigate({ to: '/' })
      }
    }
  }, [isAuthenticated, navigate, redirectTo])

  const emailRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    emailRef.current?.focus()
  }, [])

  const onSubmit: SubmitHandler<{ email: string; password: string }> = async ({ email, password }) => {
    if (!(isCaptchaDisabled || token)) {
      return toast.error(t('Captcha is required'))
    }

    try {
      if (!isCaptchaDisabled) {
        cookies.set('captcha', token, { path: '/' })
      }

      await loginWithCredentials.mutateAsync({
        email,
        password,
        captchaToken: token,
      })

      if (!isCaptchaDisabled) {
        cookies.remove('captcha', { path: '/' })
      }

      toast.success(t('Login successful'))

      await navigate({ to: '/' })
    } catch (err) {
      if (err && err.code === 'EMAIL_NOT_VERIFIED') {
        navigate({ to: '/auth/verification-email-sent', search: { email } } as never)
      } else {
        toast.error((err as Error)?.message ?? t('An error occurred'))
      }
    } finally {
      if (!isCaptchaDisabled && turnstile && token) {
        setToken('')
        turnstile.reset()
      }
    }
  }

  return (
    <div className="relative z-10 flex justify-center py-14">
      <Card className="mx-4 w-full max-w-md shadow-2xl">
        <CardHeader className="flex flex-col items-center gap-2 text-center">
          <LogoFull size="md" />
          <h1 className="mt-2 text-center font-semibold text-lg text-muted-foreground">
            {t('Sign in to your account')}
          </h1>
        </CardHeader>

        <CardContent>
          <Form formMethods={formMethods} onSubmit={onSubmit} suppressHydrationWarning>
            <div className="flex flex-col gap-4">
              <InputFormField
                autoComplete="email"
                label={t('Email')}
                name="email"
                placeholder={t('Enter your email')}
                ref={emailRef}
                required
              />

              <PasswordFormField
                autoComplete="current-password"
                label={t('Password')}
                name="password"
                placeholder={t('Enter your password')}
                required
              />

              <div className="flex w-full flex-row items-center justify-end">
                <Link className={authFooterLinkClass} to="/auth/forgot-password">
                  {t('Forgot password?')}
                </Link>
              </div>

              {!isCaptchaDisabled && (
                <ClientOnly fallback={<Skeleton className="h-[65px] w-full" />}>
                  <div className={cn('flex justify-center', turnstileSize === 'compact' ? 'pb-4' : 'pb-0')}>
                    <Turnstile
                      appearance="always"
                      fixedSize={true}
                      onVerify={(props) => setToken(props)}
                      sitekey={getAppConfig().CAPTCHA_SITEKEY}
                      size={turnstileSize}
                    />
                  </div>
                </ClientOnly>
              )}

              <Button className="w-full" disabled={!(token || isCaptchaDisabled)} size="lg" type="submit">
                {loginWithCredentials.isPending ? <Spinner className="mr-2" /> : null}
                {t('Sign In')}
              </Button>
            </div>
          </Form>
        </CardContent>

        <CardFooter className="justify-center">
          <p className="text-center text-md text-muted-foreground">
            {t("Don't have an account?")}{' '}
            <Link className={authFooterLinkClass} to="/auth/signup">
              {t('Sign Up')}
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  )
}
